/* Maison Byōbu · Voir chez moi
   Le client photographie son mur (ou choisit une photo), place les 4 coins du mur, et voit l'œuvre
   en perspective avec le cadrage choisi dans le cadreur, la lumière de sa pièce et un réglage de
   luminosité. Il peut enregistrer l'image.
   Tout se passe dans le navigateur : la photo n'est jamais envoyée ni stockée. Elle vit dans un
   canvas tant que la page est ouverte. Les champs du module n'ont pas de nom et ne sont rattachés
   à aucun formulaire : même posé dans le formulaire produit, rien n'en part avec la commande.

   Utilisation (contrat complet : docs/voir-chez-moi.md)
     <mb-voir-chez-moi data-oeuvre="dragon-qui-monte" data-nom="Le dragon qui monte"
       data-image="…/MB_cadreur_dragon-qui-monte.jpg" data-echantillon="/products/echantillon?variant=…"
       data-marge="0"></mb-voir-chez-moi>   data-le : largeur d'un lé en cm, 76,2 par défaut
     el.maj(cadrage)  le JSON _cadrage du cadreur (objet ou texte), null si les dimensions sont invalides
     el.ouvrir()
   Signaux, qui remontent le DOM : mb-vcm:commander, mb-vcm:recadrer, mb-vcm:ferme,
   mb-vcm:etape {etape}, mb-vcm:mesure {cle, …}

   Rendu : pendant un geste, l'œuvre est posée par une transformation CSS (carte graphique, fluide) ;
   au lâcher, un canvas calcule chaque pixel (perspective, lumière, luminosité). Ce canvas est aussi
   l'image enregistrée : on enregistre ce qu'on voit. Pas de WebGL (pertes d'affichage sur iOS). */

(() => {
  if (!window.customElements || customElements.get('mb-voir-chez-moi')) return;

  const VCM = {
    max: 2048, // côté long de la photo de travail, en pixels
    types: 'image/jpeg,image/png,image/webp', // sans HEIC : l'iPhone convertit alors la photo en JPEG
    sansFormulaire: 'mb-vcm-hors-formulaire', // id absent de la page : aucun formulaire ne reçoit nos champs
    leCm: 76.2, // largeur d'un lé chez l'imprimeur (30 pouces), la même que dans le cadreur
    lumiere: {
      cellules: 48, // finesse de la carte de lumière (côté long)
      centile: 0.8, // un interrupteur ou un cadre sombre ne fait pas une ombre
      ombres: 0.55, // part des ombres et dégradés de la photo reprise sur l'œuvre
      min: 0.62, max: 1.25,
      murBlanc: 0.78, // luminance d'un mur blanc bien éclairé en photo
      expoMin: 0.62, expoMax: 1.08, // l'œuvre reste lisible sur une photo sombre
      teinte: 0.3, teinteMin: 0.88, teinteMax: 1.12, // une photo jaune teinte l'œuvre, sans la noyer
    },
    loupe: { taille: 116, zoom: 2.6 },
  };
  const NBSP = ' ';
  const mbVcmDeci = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const COINS = ['en haut à gauche', 'en haut à droite', 'en bas à droite', 'en bas à gauche'];

  /* ---------- Géométrie ---------- */

  // Homographie du carré unité vers le quadrilatère q = [HG, HD, BD, BG] (Heckbert).
  // (u, v) → ((a u + b v + c) / (g u + h v + 1), (d u + e v + f) / (g u + h v + 1))
  function carreVersQuad(q) {
    const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q;
    const sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
    if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) return [x1 - x0, x3 - x0, x0, y1 - y0, y3 - y0, y0, 0, 0, 1];
    const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2;
    const den = dx1 * dy2 - dx2 * dy1;
    const g = (sx * dy2 - dx2 * sy) / den, h = (dx1 * sy - sx * dy1) / den;
    return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h, 1];
  }

  function inverse(m) {
    const [a, b, c, d, e, f, g, h, i] = m;
    const A = e * i - f * h, B = f * g - d * i, C = d * h - e * g;
    const det = a * A + b * B + c * C;
    return [A / det, (c * h - b * i) / det, (b * f - c * e) / det,
      B / det, (a * i - c * g) / det, (c * d - a * f) / det,
      C / det, (b * g - a * h) / det, (a * e - b * d) / det];
  }

  const appliquer = (m, x, y) => {
    const w = m[6] * x + m[7] * y + m[8];
    return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w];
  };
  const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const borne = (v, a, b) => Math.min(b, Math.max(a, v));

  // Les coins tournent dans le sens des aiguilles d'une montre à l'écran, sans se croiser
  function convexe(q) {
    for (let i = 0; i < 4; i++) {
      const [ax, ay] = q[i], [bx, by] = q[(i + 1) % 4], [cx, cy] = q[(i + 2) % 4];
      if ((bx - ax) * (cy - by) - (by - ay) * (cx - bx) <= 0) return false;
      if (distance(q[i], q[(i + 1) % 4]) < 8) return false;
    }
    return true;
  }

  // Proportion (largeur / hauteur) du mur réel vue à travers ses 4 coins, par la méthode de Zhang et He
  // (« Whiteboard scanning », 2006), avec la focale d'un objectif normal 1× : environ 0,72 fois le grand
  // côté de la photo (24 à 26 mm en équivalent 24 × 36). Rend NaN si les coins ne s'y prêtent pas.
  function proportionVue(q, W, H) {
    const f = 0.72 * Math.max(W, H), cx = W / 2, cy = H / 2;
    const [m1, m2, m4, m3] = q.map(([x, y]) => [x - cx, y - cy, 1]); // HG, HD, BD, BG
    const croix = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    const scal = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    const c14 = croix(m1, m4);
    const k2 = scal(c14, m3) / scal(croix(m2, m4), m3), k3 = scal(c14, m2) / scal(croix(m3, m4), m2);
    const n2 = m2.map((v, i) => k2 * v - m1[i]), n3 = m3.map((v, i) => k3 * v - m1[i]);
    const longueur = (n) => Math.sqrt((n[0] * n[0] + n[1] * n[1]) / (f * f) + n[2] * n[2]);
    return longueur(n2) / longueur(n3);
  }

  /* ---------- Lecture de la photo ---------- */

  // Taille et orientation lues dans l'en-tête du fichier, sans décoder l'image
  async function enTete(file) {
    const b = new DataView(await file.slice(0, 1 << 19).arrayBuffer());
    const n = b.byteLength;
    if (n > 24 && b.getUint32(0) === 0x89504E47) return { w: b.getUint32(16), h: b.getUint32(20), o: 1 };
    if (n > 32 && b.getUint32(0) === 0x52494646 && b.getUint32(8) === 0x57454250) {
      const c = b.getUint32(12);
      if (c === 0x56503820) return { w: b.getUint16(26, true) & 0x3fff, h: b.getUint16(28, true) & 0x3fff, o: 1 };
      if (c === 0x5650384C) { const v = b.getUint32(21, true); return { w: (v & 0x3fff) + 1, h: ((v >> 14) & 0x3fff) + 1, o: 1 }; }
      if (c === 0x56503858) return { w: 1 + (b.getUint32(24, true) & 0xffffff), h: 1 + (b.getUint32(27, true) & 0xffffff), o: 1 };
      return null;
    }
    if (n < 4 || b.getUint16(0) !== 0xFFD8) return null;
    let p = 2, o = 1;
    while (p + 9 < n) {
      if (b.getUint8(p) !== 0xFF) { p++; continue; }
      const m = b.getUint8(p + 1);
      if (m === 0xFF) { p++; continue; }
      if (m === 0x01 || (m >= 0xD0 && m <= 0xD8)) { p += 2; continue; }
      const len = b.getUint16(p + 2);
      if (m === 0xE1 && b.getUint32(p + 4) === 0x45786966) o = orientationExif(b, p + 10) || o;
      if (m >= 0xC0 && m <= 0xCF && m !== 0xC4 && m !== 0xC8 && m !== 0xCC) return { w: b.getUint16(p + 7), h: b.getUint16(p + 5), o };
      p += 2 + len;
    }
    return null;
  }

  function orientationExif(b, t) {
    if (t + 8 > b.byteLength) return 0;
    const le = b.getUint16(t) === 0x4949;
    const ifd = t + b.getUint32(t + 4, le);
    if (ifd + 2 > b.byteLength) return 0;
    for (let i = 0, nb = b.getUint16(ifd, le); i < nb; i++) {
      const e = ifd + 2 + i * 12;
      if (e + 12 > b.byteLength) return 0;
      if (b.getUint16(e, le) === 0x0112) return b.getUint16(e + 8, le);
    }
    return 0;
  }

  // Réduction par moitiés successives : une seule forte réduction crénelle l'image sur Safari
  function reduire(source, sx, sy, sw, sh, tw, th) {
    let src = source, x = sx, y = sy, w = sw, h = sh, tmp = null;
    while (w > tw * 2 && h > th * 2) {
      const c = document.createElement('canvas');
      c.width = Math.max(tw, Math.round(w / 2)); c.height = Math.max(th, Math.round(h / 2));
      const cx = c.getContext('2d');
      cx.imageSmoothingQuality = 'high';
      cx.drawImage(src, x, y, w, h, 0, 0, c.width, c.height);
      if (tmp) tmp.width = tmp.height = 1;
      tmp = src = c; x = 0; y = 0; w = c.width; h = c.height;
    }
    const out = document.createElement('canvas');
    out.width = tw; out.height = th;
    const ox = out.getContext('2d');
    ox.imageSmoothingQuality = 'high';
    ox.drawImage(src, x, y, w, h, 0, 0, tw, th);
    if (tmp) tmp.width = tmp.height = 1;
    return out;
  }

  function orienter(ctx, o, w, h) {
    if (o === 5) ctx.transform(0, 1, 1, 0, 0, 0);
    else if (o === 6) ctx.transform(0, 1, -1, 0, h, 0);
    else if (o === 7) ctx.transform(0, -1, -1, 0, h, w);
    else if (o === 8) ctx.transform(0, -1, 1, 0, 0, w);
  }

  // La photo est décodée directement en taille réduite quand le navigateur le permet :
  // une photo de 48 Mpx décodée en entier pèse près de 200 Mo et peut fermer l'onglet sur iPhone.
  async function decoder(file) {
    const info = await enTete(file).catch(() => null);
    const tourne = !!info && info.o >= 5 && info.o <= 8;
    const ow = info ? (tourne ? info.h : info.w) : 0, oh = info ? (tourne ? info.w : info.h) : 0;
    let source = null, methode = '', liberer = () => {};
    if ('createImageBitmap' in window) {
      try {
        const opts = { imageOrientation: 'from-image', resizeQuality: 'high' };
        if (ow && Math.max(ow, oh) > VCM.max) {
          const k = VCM.max / Math.max(ow, oh);
          // Une seule dimension : l'autre suit les proportions, quel que soit l'ordre rotation / réduction du navigateur
          if (ow >= oh) opts.resizeWidth = Math.round(ow * k); else opts.resizeHeight = Math.round(oh * k);
        }
        const bmp = await createImageBitmap(file, opts);
        source = bmp; liberer = () => bmp.close();
        methode = opts.resizeWidth || opts.resizeHeight ? 'createImageBitmap réduit' : 'createImageBitmap';
      } catch (e) { source = null; }
    }
    if (!source) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      try { await img.decode(); } finally { URL.revokeObjectURL(url); }
      source = img; methode = 'img'; liberer = () => { img.src = ''; };
    }
    const sw = source.naturalWidth || source.width, sh = source.naturalHeight || source.height;
    // Si le navigateur a ignoré l'orientation EXIF, on tourne nous-mêmes
    const corriger = tourne && methode !== 'img' && info.w !== info.h && (sw > sh) === (info.w > info.h) ? info.o : 0;
    const dw = corriger ? sh : sw, dh = corriger ? sw : sh;
    const k = Math.min(1, VCM.max / Math.max(dw, dh));
    const W = Math.max(1, Math.round(dw * k)), H = Math.max(1, Math.round(dh * k));
    let canvas;
    if (!corriger) canvas = reduire(source, 0, 0, sw, sh, W, H);
    else {
      const r = reduire(source, 0, 0, sw, sh, H, W);
      canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const c = canvas.getContext('2d');
      orienter(c, corriger, r.width, r.height);
      c.drawImage(r, 0, 0);
      r.width = r.height = 1;
    }
    liberer();
    return { canvas, W, H, methode, original: info ? `${ow}×${oh}` : `${sw}×${sh}`, orientation: info ? info.o : 0 };
  }

  // Carte de lumière de la photo : le 80e centile de luminance par cellule (les petits objets
  // sombres n'y laissent pas de trace), adoucie, et la couleur moyenne de chaque cellule.
  function carteLumiere(ctx, W, H) {
    const n = VCM.lumiere.cellules, k = n / Math.max(W, H);
    const cw = Math.max(2, Math.round(W * k)), ch = Math.max(2, Math.round(H * k));
    const d = ctx.getImageData(0, 0, W, H).data;
    const colonne = new Uint16Array(W);
    for (let x = 0; x < W; x++) colonne[x] = Math.min(cw - 1, Math.floor((x * cw) / W));
    const hist = new Uint32Array(cw * ch * 32), somme = new Float64Array(cw * ch * 3), nb = new Uint32Array(cw * ch);
    for (let y = 0, o = 0; y < H; y++) {
      const ligne = Math.min(ch - 1, Math.floor((y * ch) / H)) * cw;
      for (let x = 0; x < W; x++, o += 4) {
        const c = ligne + colonne[x], r = d[o], g = d[o + 1], b = d[o + 2];
        hist[c * 32 + (((r * 54 + g * 183 + b * 19) >> 8) >> 3)]++;
        somme[c * 3] += r; somme[c * 3 + 1] += g; somme[c * 3 + 2] += b; nb[c]++;
      }
    }
    let carte = new Float32Array(cw * ch);
    const couleur = new Float32Array(cw * ch * 3);
    for (let c = 0; c < cw * ch; c++) {
      const seuil = nb[c] * VCM.lumiere.centile;
      let cumul = 0, i = 0;
      for (; i < 31; i++) { cumul += hist[c * 32 + i]; if (cumul >= seuil) break; }
      carte[c] = ((i + 0.5) * 8) / 255;
      for (let j = 0; j < 3; j++) couleur[c * 3 + j] = nb[c] ? somme[c * 3 + j] / nb[c] / 255 : 0;
    }
    for (let passe = 0; passe < 2; passe++) {
      const flou = new Float32Array(cw * ch);
      for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) {
        let s = 0, m = 0;
        for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
          const xx = x + i, yy = y + j;
          if (xx >= 0 && yy >= 0 && xx < cw && yy < ch) { s += carte[yy * cw + xx]; m++; }
        }
        flou[y * cw + x] = s / m;
      }
      carte = flou;
    }
    return { cw, ch, carte, couleur };
  }

  /* ---------- Le module ---------- */

  class MbVoirChezMoi extends HTMLElement {
    connectedCallback() {
      if (this.pret) return;
      this.pret = true;
      this.cadrage = null; this.photo = null; this.coins = null; this.texture = null;
      this.lum = 1; this.lumCuite = 1; this.les = false; this.expo = 1;
      this.version = 0; this.versionPhoto = 0; this.fichier = null; this.mesures = {};
      this.uid = `vcm${Math.random().toString(36).slice(2, 8)}`;
      this.surRetour = () => this.fermer({ historique: true });
      addEventListener('pagehide', () => this.arreterDetourage()); // le moteur reste prêt tant que la page est ouverte
      this.declencheur = this.querySelector('[data-vcm-ouvrir]');
      if (!this.declencheur && this.dataset.bouton !== 'non') this.dessinerDeclencheur();
      if (this.declencheur) this.declencheur.addEventListener('click', () => this.ouvrir());
      this.etatDeclencheur();
    }

    get nom() { return this.dataset.nom || ''; }
    get coinsPhoto() { return this.coins ? this.coins.map((p) => [...p]) : null; }

    signal(nom, detail = {}) {
      this.dispatchEvent(new CustomEvent(`mb-vcm:${nom}`, { bubbles: true, composed: true, detail }));
    }

    mesure(cle, valeurs) {
      this.mesures[cle] = valeurs;
      this.signal('mesure', { cle, ...valeurs });
    }

    /* ---------- Le bouton du cadreur ---------- */
    dessinerDeclencheur() {
      const aide = `${this.uid}-aide`;
      this.insertAdjacentHTML('afterbegin', `
        <div class="mb-vcm-declencheur">
          <button type="button" class="mb-btn mb-vcm-declencheur__btn" data-vcm-ouvrir aria-describedby="${aide}"><span>Voir chez moi</span></button>
          <p class="mb-vcm-declencheur__aide" id="${aide}" data-vcm-aide></p>
        </div>`);
      this.declencheur = this.querySelector('[data-vcm-ouvrir]');
    }

    etatDeclencheur() {
      if (!this.declencheur) return;
      const ok = !!this.cadrage;
      this.declencheur.setAttribute('aria-disabled', String(!ok));
      const aide = this.querySelector('[data-vcm-aide]');
      if (aide) aide.textContent = ok ? `Photographiez votre mur${NBSP}: l’œuvre s’y pose, avec votre cadrage.` : 'Indiquez d’abord les dimensions de votre mur.';
    }

    /* ---------- La démo de 3 secondes : photographier, placer les coins, toucher le meuble ----------
       Un dessin du salon de synthèse, l'œuvre posée derrière le canapé. Elle se joue au premier lancement
       sur cet appareil, s'arrête d'un toucher, et se revoit par « Revoir la démo ». Sans animation si le
       visiteur l'a demandé : l'image finale et ses trois légendes. */
    demo() {
      const image = (this.dataset.image || '').replace(/"/g, '&quot;');
      return `<div class="mb-vcm__demo" data-vcm-demo>
        <p class="mb-vcm__demo-exemple">Exemple</p>
        <svg viewBox="0 0 320 240" role="img" aria-label="Démonstration : photographier le mur, placer les 4 ronds sur ses coins, toucher le meuble">
          <defs><clipPath id="${this.uid}-mur"><polygon points="50,30 278,38 274,176 54,186"/></clipPath></defs>
          <rect width="320" height="240" fill="#E4E0D8"/>
          <polygon points="0,0 320,0 320,24 278,38 50,30" fill="#EFECE6"/>
          <polygon points="0,0 50,30 54,186 0,212" fill="#D7D1C6"/>
          <polygon points="278,38 320,24 320,200 274,176" fill="#CEC7BA"/>
          <polygon points="50,30 278,38 274,176 54,186" fill="#ECE8E1"/>
          <polygon points="0,212 54,186 274,176 320,200 320,240 0,240" fill="#9C7B5B"/>
          <image class="mb-vcm__demo-oeuvre" href="${image}" x="50" y="30" width="228" height="156" preserveAspectRatio="xMidYMid slice" clip-path="url(#${this.uid}-mur)"/>
          <polygon points="86,140 234,138 236,158 246,158 247,202 238,206 82,208 74,204 73,160 84,160" fill="#5E6B63"/>
          <polygon points="86,170 234,168 235,180 86,182" fill="#6B786F"/>
          <polygon points="94,146 156,145 158,169 94,170" fill="#6F7C73"/>
          <polygon points="160,145 226,144 227,168 161,169" fill="#6F7C73"/>
          <g class="mb-vcm__demo-coins" fill="#C7A35E" stroke="#FAFAF8" stroke-width="2">
            <circle cx="50" cy="30" r="6"/><circle cx="278" cy="38" r="6"/><circle cx="274" cy="176" r="6"/><circle cx="54" cy="186" r="6"/>
          </g>
          <circle class="mb-vcm__demo-doigt" cx="160" cy="186" r="13" fill="none" stroke="#FAFAF8" stroke-width="3"/>
          <rect class="mb-vcm__demo-flash" width="320" height="240" fill="#FAFAF8"/>
        </svg>
        <ol class="mb-vcm__demo-legendes"><li>1.${NBSP}Photographiez</li><li>2.${NBSP}Placez les coins</li><li>3.${NBSP}Touchez le meuble</li></ol>
      </div>`;
    }

    demoVue() { try { return localStorage.getItem('mb-vcm-demo') === 'vue'; } catch (e) { return true; } }

    jouerDemo() {
      const d = this.el && this.el.demo;
      if (!d || (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
      d.removeAttribute('data-joue');
      void d.offsetWidth; // relance l'animation
      d.setAttribute('data-joue', '');
      this.dialog.setAttribute('data-demo', '');
      clearTimeout(this.minuteurDemo);
      this.minuteurDemo = setTimeout(() => this.finirDemo(), 3400);
      try { localStorage.setItem('mb-vcm-demo', 'vue'); } catch (e) { /* stockage indisponible : elle se rejouera */ }
    }

    finirDemo() {
      clearTimeout(this.minuteurDemo);
      if (this.el && this.el.demo) this.el.demo.removeAttribute('data-joue'); // l'image finale reste
      if (this.dialog) this.dialog.removeAttribute('data-demo');
    }

    // Relevé d'usage anonyme de la boutique (événements clients de Shopify) : jamais une donnée de la photo
    suivre(evenement) {
      try {
        const a = window.Shopify && window.Shopify.analytics;
        if (a && typeof a.publish === 'function') a.publish(`mb_voir_chez_moi_${evenement}`, { oeuvre: this.dataset.oeuvre || '' });
      } catch (e) { /* le relevé ne doit jamais gêner le client */ }
    }

    /* ---------- Cadrage reçu du cadreur ---------- */
    maj(cadrage) {
      let c = cadrage;
      if (typeof c === 'string') { try { c = JSON.parse(c); } catch (e) { c = null; } }
      const ok = !!c && [c.x0, c.y0, c.x1, c.y1, c.largeur_cm, c.hauteur_cm].every(Number.isFinite)
        && c.x1 > c.x0 && c.y1 > c.y0 && c.largeur_cm > 0 && c.hauteur_cm > 0;
      this.cadrage = ok ? { ...c } : null;
      this.etatDeclencheur();
      if (this.el) this.el.nom.textContent = this.titre();
      if (this.dialog && this.dialog.open && this.cadrage) this.preparerOeuvre();
    }

    dimensions() {
      if (!this.cadrage) return '';
      const m = (cm) => mbVcmDeci.format(cm / 100);
      return `${m(this.cadrage.largeur_cm)}${NBSP}×${NBSP}${m(this.cadrage.hauteur_cm)}${NBSP}m`;
    }

    titre() { return [this.nom, this.dimensions()].filter(Boolean).join(' · '); }

    marge() {
      if (!this.cadrage) return 0;
      const brute = this.cadrage.marge_cm != null ? this.cadrage.marge_cm : this.dataset.marge;
      const m = parseFloat(brute);
      return Number.isFinite(m) && m > 0 ? Math.min(m, Math.min(this.cadrage.largeur_cm, this.cadrage.hauteur_cm) * 0.5) : 0;
    }

    proportionMur() {
      const m = this.marge();
      return (this.cadrage.largeur_cm - m) / (this.cadrage.hauteur_cm - m);
    }

    /* ---------- La couche ---------- */
    construire() {
      if (this.dialog) return;
      const id = this.uid, f = VCM.sansFormulaire;
      const echantillon = this.dataset.echantillon
        ? `<a class="mb-lien" href="${this.dataset.echantillon}">recevoir le grand échantillon</a>` : 'recevoir le grand échantillon';
      const coins = COINS.map((c, i) => `<button type="button" class="mb-vcm__coin" data-coin="${i}" aria-label="Coin ${c} du mur. Faites glisser, ou utilisez les flèches du clavier."></button>`).join('');
      this.insertAdjacentHTML('beforeend', `
        <dialog class="mb mb-vcm" aria-labelledby="${id}-titre" data-etape="photo">
          <div class="mb-vcm__tete">
            <p class="mb-vcm__titre" id="${id}-titre">Voir chez moi <span class="mb-vcm__nom" data-vcm-nom></span></p>
            <button type="button" class="mb-vcm__fermer" data-vcm-fermer aria-label="Fermer">×</button>
          </div>
          <div class="mb-vcm__scene" data-vcm-scene>
            <div class="mb-vcm__accueil" data-vcm-accueil>${this.demo()}</div>
            <div class="mb-vcm__plan" data-vcm-plan hidden>
              <canvas class="mb-vcm__photo" data-vcm-photo></canvas>
              <canvas class="mb-vcm__brouillon" data-vcm-brouillon aria-hidden="true"></canvas>
              <canvas class="mb-vcm__rendu" data-vcm-rendu role="img" aria-label="Votre mur avec l’œuvre"></canvas>
              <canvas class="mb-vcm__premier" data-vcm-premier aria-hidden="true"></canvas>
              <svg class="mb-vcm__traits" data-vcm-traits aria-hidden="true" preserveAspectRatio="none">
                <g class="mb-vcm__les" data-vcm-les></g>
                <polygon class="mb-vcm__contour" data-vcm-contour points="0,0"></polygon>
              </svg>
              ${coins}
              <span class="mb-vcm__pouls" aria-hidden="true"><span class="mb-vcm__pouls-rond"></span><span class="mb-vcm__pouls-texte">Touchez le meuble</span></span>
            </div>
            <p class="mb-vcm__attente" data-vcm-attente hidden>Ouverture de votre photo…</p>
            <div class="mb-vcm__loupe" data-vcm-loupe hidden><canvas></canvas></div>
          </div>
          <div class="mb-vcm__reglages">
            <p class="mb-vcm__numero" data-vcm-numero><span class="mb-vcm__barres" aria-hidden="true"><i></i><i></i><i></i></span><span data-vcm-numero-texte></span></p>
            <div class="mb-vcm__groupe" data-vcm-pour="photo">
              <h2 class="mb-vcm__consigne-grande" tabindex="-1">Photographiez votre mur</h2>
              <p class="mb-vcm__aide">De face, en entier, téléphone à l’horizontale. Votre photo reste sur votre appareil.</p>
              <label class="mb-btn mb-btn--clair mb-btn--large mb-vcm__fichier mb-vcm__prendre">
                <input type="file" accept="${VCM.types}" capture="environment" form="${f}" data-vcm-fichier>
                <span>Prendre une photo</span>
              </label>
              <label class="mb-btn mb-btn--large mb-vcm__fichier mb-vcm__contour-btn">
                <input type="file" accept="${VCM.types}" form="${f}" data-vcm-fichier>
                <span>Choisir une photo</span>
              </label>
              <p class="mb-vcm__erreur" role="alert" data-vcm-erreur hidden></p>
              <p class="mb-vcm__second"><button type="button" class="mb-lien" data-vcm-reprendre hidden>Revenir à ma photo</button><button type="button" class="mb-lien" data-vcm-demo-bouton>Revoir la démo</button></p>
            </div>
            <div class="mb-vcm__groupe" data-vcm-pour="coins">
              <div class="mb-vcm__avec-schema">
                <svg class="mb-vcm__schema" viewBox="0 0 64 46" aria-hidden="true" focusable="false"><rect x="6" y="5" width="52" height="34" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.5"/><rect x="17" y="27" width="30" height="14" rx="2" fill="currentColor" opacity="0.35"/><circle cx="6" cy="5" r="3.2"/><circle cx="58" cy="5" r="3.2"/><circle cx="58" cy="39" r="3.2"/><circle cx="6" cy="39" r="3.2"/></svg>
                <h2 class="mb-vcm__consigne-grande" tabindex="-1">Placez les 4 ronds sur les coins de votre mur</h2>
              </div>
              <p class="mb-vcm__aide">Un coin caché par un meuble${NBSP}? Placez le rond là où il serait.</p>
              <p class="mb-vcm__alerte" data-vcm-alerte hidden>Les coins se croisent${NBSP}: vérifiez que chaque rond est à son coin.</p>
              <p class="mb-vcm__discret" data-vcm-proportion hidden></p>
              <button type="button" class="mb-btn mb-btn--clair mb-btn--large" data-vcm-valider><span>Continuer</span></button>
            </div>
            <div class="mb-vcm__groupe" data-vcm-pour="question">
              <h2 class="mb-vcm__consigne-grande" tabindex="-1">Y a-t-il un meuble devant votre mur${NBSP}?</h2>
              <div class="mb-vcm__ouinon">
                <button type="button" class="mb-btn mb-btn--clair mb-btn--large" data-vcm-oui><span>Oui</span></button>
                <button type="button" class="mb-btn mb-btn--clair mb-btn--large" data-vcm-non><span>Non</span></button>
              </div>
            </div>
            <div class="mb-vcm__groupe" data-vcm-pour="rendu">
              <div class="mb-vcm__ligne">
                <label class="mb-vcm__curseur">
                  <span>Luminosité de l’œuvre</span>
                  <input type="range" min="50" max="150" step="1" value="100" form="${f}" data-vcm-lum aria-valuetext="Normale">
                </label>
                <button type="button" class="mb-vcm__bascule" data-vcm-les-bouton aria-pressed="false">Voir les lés</button>
              </div>
              <button type="button" class="mb-btn mb-btn--large mb-vcm__secondaire" data-vcm-meubles><span>Mettre l’œuvre derrière un meuble</span></button>
              <button type="button" class="mb-btn mb-btn--clair mb-btn--large" data-vcm-commander><span>Je compose mon mur</span></button>
              <ul class="mb-vcm__actions">
                <li><button type="button" class="mb-lien" data-vcm-enregistrer aria-disabled="true">Enregistrer l’image</button></li>
                <li><button type="button" class="mb-lien" data-vcm-ajuster>Ajuster les coins</button></li>
                <li><button type="button" class="mb-lien" data-vcm-recadrer>Changer le cadrage</button></li>
                <li><button type="button" class="mb-lien" data-vcm-autre>Changer de photo</button></li>
              </ul>
              <p class="mb-vcm__note">Aperçu indicatif${NBSP}: les couleurs dépendent de votre écran et de votre lumière. Pour juger la couleur et la matière${NBSP}: ${echantillon}.</p>
            </div>
            <div class="mb-vcm__groupe" data-vcm-pour="meubles">
              <h2 class="mb-vcm__consigne-grande" tabindex="-1" data-vcm-consigne-meubles aria-live="polite">Touchez le meuble pour le faire passer devant l’œuvre</h2>
              <p class="mb-vcm__etat" role="status" aria-live="polite" data-vcm-etat-ia></p>
              <button type="button" class="mb-btn mb-btn--clair mb-btn--large" data-vcm-termine><span>Terminer</span></button>
              <p class="mb-vcm__second">
                <button type="button" class="mb-lien" data-vcm-annuler aria-disabled="true">Annuler le dernier geste</button>
                <button type="button" class="mb-lien" data-vcm-corriger aria-expanded="false">Corriger au pinceau</button>
              </p>
              <div class="mb-vcm__corriger" data-vcm-corriger-bloc hidden>
                <div class="mb-vcm__outils" role="group" aria-label="Corriger le premier plan">
                  <button type="button" class="mb-vcm__outil" data-vcm-outil="toucher" aria-pressed="true" aria-label="Toucher un meuble : il passe devant l’œuvre">Toucher</button>
                  <button type="button" class="mb-vcm__outil" data-vcm-outil="ajouter" aria-pressed="false" aria-label="Pinceau : ajouter au premier plan">Ajouter</button>
                  <button type="button" class="mb-vcm__outil" data-vcm-outil="retirer" aria-pressed="false" aria-label="Pinceau : retirer du premier plan">Retirer</button>
                </div>
                <label class="mb-vcm__curseur" data-vcm-taille-bloc>
                  <span>Taille du pinceau</span>
                  <input type="range" min="6" max="48" step="1" value="18" form="${f}" data-vcm-taille>
                </label>
              </div>
            </div>
            <p class="mb-visuellement-cache" aria-live="polite" data-vcm-annonce></p>
          </div>
        </dialog>`);
      const d = this.dialog = this.querySelector('dialog.mb-vcm');
      const q = (s) => d.querySelector(s);
      this.el = {
        nom: q('[data-vcm-nom]'), scene: q('[data-vcm-scene]'), accueil: q('[data-vcm-accueil]'), plan: q('[data-vcm-plan]'),
        photo: q('[data-vcm-photo]'), brouillon: q('[data-vcm-brouillon]'), rendu: q('[data-vcm-rendu]'),
        traits: q('[data-vcm-traits]'), lesG: q('[data-vcm-les]'), contour: q('[data-vcm-contour]'),
        coins: [...d.querySelectorAll('[data-coin]')], attente: q('[data-vcm-attente]'), loupe: q('[data-vcm-loupe]'),
        erreur: q('[data-vcm-erreur]'), reprendre: q('[data-vcm-reprendre]'), alerte: q('[data-vcm-alerte]'),
        valider: q('[data-vcm-valider]'), lum: q('[data-vcm-lum]'), lesBouton: q('[data-vcm-les-bouton]'),
        enregistrer: q('[data-vcm-enregistrer]'), annonce: q('[data-vcm-annonce]'),
        premier: q('[data-vcm-premier]'), outils: [...d.querySelectorAll('[data-vcm-outil]')], annuler: q('[data-vcm-annuler]'),
        taille: q('[data-vcm-taille]'), etatIA: q('[data-vcm-etat-ia]'), proportion: q('[data-vcm-proportion]'),
        termine: q('[data-vcm-termine]'), numero: q('[data-vcm-numero]'), numeroTexte: q('[data-vcm-numero-texte]'),
        consigneMeubles: q('[data-vcm-consigne-meubles]'), corriger: q('[data-vcm-corriger]'), corrigerBloc: q('[data-vcm-corriger-bloc]'),
        demo: q('[data-vcm-demo]'),
      };

      d.addEventListener('close', () => this.apresFermeture());
      q('[data-vcm-fermer]').addEventListener('click', () => this.fermer());
      for (const champ of d.querySelectorAll('[data-vcm-fichier]')) {
        champ.addEventListener('change', () => {
          const fichier = champ.files && champ.files[0];
          champ.value = ''; // rien ne reste attaché au champ
          if (fichier) this.chargerPhoto(fichier);
        });
      }
      this.el.reprendre.addEventListener('click', () => this.etape('rendu'));
      // Étape 2, « Continuer » : la question du meuble, une fois par photo (jamais d'œuvre posée
      // d'abord par-dessus le canapé), puis le résultat
      this.el.valider.addEventListener('click', () => {
        if (!convexe(this.coins)) return;
        if (!this.questionFaite) { this.etape('question'); return; }
        this.etape('rendu');
        q('[data-vcm-commander]').focus({ preventScroll: true });
      });
      q('[data-vcm-oui]').addEventListener('click', () => {
        this.questionFaite = true;
        if (!(this.ia && this.ia.etat === 'indisponible')) this.choisirOutil('toucher');
        this.etape('meubles');
      });
      q('[data-vcm-non]').addEventListener('click', () => {
        this.questionFaite = true;
        this.etape('rendu');
        q('[data-vcm-commander]').focus({ preventScroll: true });
      });
      this.el.corriger.addEventListener('click', () => this.ouvrirCorriger(this.el.corrigerBloc.hidden));
      q('[data-vcm-demo-bouton]').addEventListener('click', () => this.jouerDemo());
      this.el.demo.addEventListener('pointerdown', () => this.finirDemo());
      q('[data-vcm-commander]').addEventListener('click', () => { this.suivre('vivre'); this.fermer({ suite: 'commander' }); });
      q('[data-vcm-recadrer]').addEventListener('click', () => this.fermer({ suite: 'recadrer' }));
      q('[data-vcm-ajuster]').addEventListener('click', () => this.etape('coins'));
      q('[data-vcm-autre]').addEventListener('click', () => this.etape('photo'));
      this.el.enregistrer.addEventListener('click', () => this.enregistrer());
      this.el.lesBouton.addEventListener('click', () => {
        this.les = !this.les;
        this.el.lesBouton.setAttribute('aria-pressed', String(this.les));
        this.dialog.toggleAttribute('data-les', this.les);
        this.version++; this.fichier = null; this.etatEnregistrer(); this.planifierExport();
      });
      this.el.lum.addEventListener('input', () => {
        this.lum = this.el.lum.value / 100;
        const ecart = Math.round((this.lum - 1) * 100);
        this.el.lum.setAttribute('aria-valuetext', ecart === 0 ? 'Normale' : `${ecart > 0 ? '+' : '−'}${Math.abs(ecart)}${NBSP}%`);
        this.el.rendu.style.filter = `brightness(${(this.lum / this.lumCuite).toFixed(3)})`;
        this.el.brouillon.style.filter = `brightness(${(this.lum * this.expo).toFixed(3)})`;
      });
      this.el.lum.addEventListener('change', () => this.planifierRendu(120));
      q('[data-vcm-meubles]').addEventListener('click', () => this.etape('meubles'));
      q('[data-vcm-termine]').addEventListener('click', () => { this.etape('rendu'); q('[data-vcm-commander]').focus({ preventScroll: true }); });
      this.el.outils.forEach((b) => b.addEventListener('click', () => this.choisirOutil(b.dataset.vcmOutil)));
      this.el.annuler.addEventListener('click', () => this.annuler());
      this.choisirOutil('toucher');
      this.brancherGestes();
      if ('ResizeObserver' in window) new ResizeObserver(() => this.disposer()).observe(this.el.scene);
    }

    async ouvrir() {
      if (!this.cadrage) return;
      this.construire();
      if (this.dialog.open) return;
      this.el.nom.textContent = this.titre();
      this.dialog.showModal();
      document.documentElement.classList.add('mb-vcm-ouvert');
      // Le geste retour (ou la touche retour d'Android) ferme la couche au lieu de quitter la fiche
      history.pushState({ mbVcm: this.uid }, '');
      addEventListener('popstate', this.surRetour);
      this.suite = ''; this.parHistorique = false;
      this.signal('etape', { etape: 'ouvert' });
      this.suivre('ouvert');
      this.etape(this.photo ? 'rendu' : 'photo');
      if (!this.photo && !this.demoVue()) this.jouerDemo();
      if (this.photo) await this.preparerOeuvre();
      else this.chargerOeuvre().catch(() => {}); // on précharge l'œuvre pendant la prise de vue
    }

    fermer({ historique = false, suite = '' } = {}) {
      if (!this.dialog || !this.dialog.open) return;
      this.suite = suite; this.parHistorique = historique;
      this.dialog.close();
    }

    apresFermeture() {
      removeEventListener('popstate', this.surRetour);
      document.documentElement.classList.remove('mb-vcm-ouvert');
      this.cacherLoupe();
      this.sortirMeubles();
      const suite = this.suite;
      const envoyer = () => { this.signal('ferme'); if (suite) this.signal(suite, { cadrage: this.cadrage }); };
      if (!this.parHistorique && history.state && history.state.mbVcm === this.uid) {
        // Le retour d'historique remet la page à sa position d'avant l'ouverture (Horizon, sous 990 px) :
        // les signaux partent après lui, sinon le cadreur défile vers la case puis la page revient en arrière
        let fait = false;
        const apres = () => { if (fait) return; fait = true; removeEventListener('popstate', apres); requestAnimationFrame(envoyer); };
        addEventListener('popstate', apres);
        setTimeout(apres, 600);
        history.back();
      } else envoyer();
      this.suite = ''; this.parHistorique = false;
    }

    etape(nom) {
      const d = this.dialog;
      if (d.dataset.etape === 'meubles' && nom !== 'meubles') this.sortirMeubles();
      d.dataset.etape = nom;
      const n = { photo: 1, coins: 2, question: 3, meubles: 3 }[nom];
      this.el.numero.hidden = !n;
      this.el.numero.dataset.n = n || '';
      this.el.numeroTexte.textContent = n ? `Étape ${n} sur 3` : '';
      if (nom !== 'photo') this.finirDemo();
      this.el.accueil.hidden = nom !== 'photo';
      this.el.plan.hidden = nom === 'photo' || !this.photo;
      this.el.reprendre.hidden = !(nom === 'photo' && this.photo);
      this.el.annonce.textContent = {
        rendu: 'L’œuvre est posée sur votre mur.',
      }[nom] || '';
      if (nom === 'meubles') this.entrerMeubles();
      if (nom === 'rendu') this.majBoutonMeubles();
      if (nom !== 'photo') requestAnimationFrame(() => this.disposer());
      // L'attention va à la consigne de l'écran : le lecteur d'écran la lit, le clavier repart de là
      const consigne = d.querySelector(`[data-vcm-pour="${nom}"] .mb-vcm__consigne-grande`);
      if (consigne) requestAnimationFrame(() => consigne.focus({ preventScroll: true }));
      this.signal('etape', { etape: nom });
    }

    attente(oui) {
      this.el.attente.hidden = !oui;
      this.dialog.toggleAttribute('data-attente', oui);
    }

    erreur(texte) {
      this.el.erreur.textContent = texte;
      this.el.erreur.hidden = !texte;
    }

    /* ---------- La photo ---------- */
    async chargerPhoto(file) {
      const v = ++this.versionPhoto;
      this.erreur('');
      if (!file || (file.type && !/^image\//.test(file.type))) {
        this.erreur('Ce fichier n’est pas une photo. Choisissez une photo de votre mur.');
        return;
      }
      if (this.dialog && this.dialog.dataset.etape !== 'photo') this.etape('photo');
      this.attente(true);
      try {
        const t0 = performance.now();
        const r = await decoder(file);
        if (v !== this.versionPhoto) { r.canvas.width = r.canvas.height = 1; return; }
        const t1 = performance.now();
        const lumiere = carteLumiere(r.canvas.getContext('2d'), r.W, r.H);
        const t2 = performance.now();
        this.oublierPhoto();
        r.canvas.className = 'mb-vcm__photo';
        r.canvas.setAttribute('data-vcm-photo', '');
        this.el.photo.replaceWith(r.canvas);
        this.el.photo = r.canvas;
        this.photo = { canvas: r.canvas, W: r.W, H: r.H, lumiere };
        this.el.rendu.width = r.W; this.el.rendu.height = r.H;
        this.el.premier.width = r.W; this.el.premier.height = r.H;
        this.el.traits.setAttribute('viewBox', `0 0 ${r.W} ${r.H}`);
        this.masque = null; this.historique = [];
        this.etatAnnuler();
        this.questionFaite = false;
        if (this.ia && this.ia.worker) this.envoyerPhoto();
        else if (this.configDetourage()) this.demarrerDetourage(); // l'outil se prépare pendant les coins
        this.coins = this.coinsParDefaut();
        this.texture = null;
        this.dialog.dataset.rendu = '';
        this.mesure('photo', {
          original: r.original, travail: `${r.W}×${r.H}`, methode: r.methode, orientation: r.orientation,
          octets: file.size, type: file.type || '?', decodage_ms: Math.round(t1 - t0), lumiere_ms: Math.round(t2 - t1),
        });
        this.attente(false);
        this.suivre('photo');
        this.etape('coins');
        await this.preparerOeuvre();
      } catch (e) {
        if (v !== this.versionPhoto) return;
        this.attente(false);
        this.erreur(`Cette photo ne s’ouvre pas ici. Choisissez une photo au format JPEG ou PNG, ou prenez-la avec le bouton «${NBSP}Prendre une photo${NBSP}».`);
        this.signal('etape', { etape: 'erreur', raison: String(e && e.message || e) });
      }
    }

    oublierPhoto() {
      if (this.photo) this.photo.canvas.width = this.photo.canvas.height = 1;
      this.photo = null;
    }

    coinsParDefaut() {
      const { W, H } = this.photo, ar = this.proportionMur();
      let w = W * 0.64, h = w / ar;
      if (h > H * 0.6) { h = H * 0.6; w = h * ar; }
      const cx = W / 2, cy = H * 0.47;
      return [[cx - w / 2, cy - h / 2], [cx + w / 2, cy - h / 2], [cx + w / 2, cy + h / 2], [cx - w / 2, cy + h / 2]];
    }

    placerCoins(points) {
      if (!this.photo || !Array.isArray(points) || points.length !== 4) return;
      this.coins = points.map((p) => this.borner(p));
      this.placerTout();
      this.planifierRendu();
    }

    /* ---------- L'œuvre et son cadrage ---------- */
    chargerOeuvre() {
      const url = this.dataset.image;
      if (this.oeuvreUrl === url && this.oeuvrePromesse) return this.oeuvrePromesse;
      this.oeuvreUrl = url; this.oeuvreImg = null; this.texture = null;
      this.oeuvrePromesse = new Promise((ok, ko) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = () => { if (this.oeuvreUrl === url) this.oeuvreImg = img; ok(img); };
        img.onerror = () => { if (this.oeuvreUrl === url) this.oeuvrePromesse = null; ko(new Error('image de l’œuvre')); };
        img.src = url;
      });
      return this.oeuvrePromesse;
    }

    async preparerOeuvre() {
      if (!this.cadrage || !this.photo) return;
      try { await this.chargerOeuvre(); } catch (e) {
        this.erreur('L’image de l’œuvre ne se charge pas. Vérifiez votre connexion, puis réessayez.');
        return;
      }
      this.texture = null;
      this.construireTexture();
      this.disposer();
      this.planifierRendu();
    }

    // Zone de l'image de l'œuvre visible sur le mur : le cadrage, moins la marge ajoutée à l'impression
    source() {
      const c = this.cadrage, img = this.oeuvreImg, m = this.marge();
      const Wa = img.naturalWidth, Ha = img.naturalHeight;
      const mu = m / 2 / c.largeur_cm, mv = m / 2 / c.hauteur_cm;
      return {
        sx: (c.x0 + (c.x1 - c.x0) * mu) * Wa, sy: (c.y0 + (c.y1 - c.y0) * mv) * Ha,
        sw: (c.x1 - c.x0) * (1 - 2 * mu) * Wa, sh: (c.y1 - c.y0) * (1 - 2 * mv) * Ha,
      };
    }

    // Texture de rendu à la taille du mur dans la photo (pas de crénelage), brouillon à 1024 px au plus
    construireTexture() {
      if (!this.oeuvreImg || !this.coins) return;
      const s = this.source(), q = this.coins;
      const cleSource = [s.sx, s.sy, s.sw, s.sh].map((v) => Math.round(v)).join(',');
      const lw = Math.max(distance(q[0], q[1]), distance(q[3], q[2]));
      const lh = Math.max(distance(q[0], q[3]), distance(q[1], q[2]));
      const tw = Math.round(borne(lw, 16, Math.min(s.sw, VCM.max))), th = Math.round(borne(lh, 16, Math.min(s.sh, VCM.max)));
      const t = this.texture;
      if (t && t.cle === cleSource && Math.abs(t.w / tw - 1) < 0.12 && Math.abs(t.h / th - 1) < 0.12) return;
      const neuve = !t || t.cle !== cleSource;
      const toile = reduire(this.oeuvreImg, s.sx, s.sy, s.sw, s.sh, tw, th);
      try {
        this.texture = { cle: cleSource, w: tw, h: th, data: toile.getContext('2d').getImageData(0, 0, tw, th).data };
        this.sansExport = false;
      } catch (e) {
        // Image servie par un autre domaine sans autorisation : on garde la pose CSS, sans export
        this.texture = { cle: cleSource, w: tw, h: th, data: null };
        this.sansExport = true;
      }
      toile.width = toile.height = 1;
      if (neuve) {
        const k = Math.min(1, 1024 / Math.max(s.sw, s.sh));
        const dw = Math.max(8, Math.round(s.sw * k)), dh = Math.max(8, Math.round(s.sh * k));
        const b = this.el.brouillon, r = reduire(this.oeuvreImg, s.sx, s.sy, s.sw, s.sh, dw, dh);
        b.width = dw; b.height = dh; b.style.width = `${dw}px`; b.style.height = `${dh}px`;
        b.getContext('2d').drawImage(r, 0, 0);
        r.width = r.height = 1;
        this.dialog.dataset.rendu = '';
      }
      this.dialog.toggleAttribute('data-sans-export', !!this.sansExport);
    }

    /* ---------- Mise en page de la scène ---------- */
    disposer() {
      if (!this.photo || !this.dialog || !this.dialog.open || this.el.plan.hidden) return;
      const r = this.el.scene.getBoundingClientRect(), cs = getComputedStyle(this.el.scene);
      const lw = r.width - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight); // encoche en paysage
      if (lw < 10 || r.height < 10) return;
      const pad = lw < 600 ? 24 : 44;
      this.pad = pad;
      this.s = Math.max(0.01, Math.min((lw - 2 * pad) / this.photo.W, (r.height - 2 * pad) / this.photo.H));
      this.el.plan.style.width = `${this.photo.W * this.s}px`;
      this.el.plan.style.height = `${this.photo.H * this.s}px`;
      this.placerTout();
    }

    borner([x, y]) {
      // Un coin peut sortir un peu de la photo (mur coupé au cadrage), son rond reste entier à l'écran
      const m = Math.max(0, (this.pad || 24) - 14) / (this.s || 1), { W, H } = this.photo;
      return [borne(x, -m, W + m), borne(y, -m, H + m)];
    }

    placerTout() {
      if (!this.photo || !this.coins || !this.s) return;
      const s = this.s, q = this.coins;
      this.el.coins.forEach((b, i) => { b.style.transform = `translate(${q[i][0] * s}px, ${q[i][1] * s}px)`; });
      const b = this.el.brouillon;
      if (b.width > 8) {
        const m = carreVersQuad(q.map(([x, y]) => [x * s, y * s])), dw = b.width, dh = b.height;
        b.style.transform = `matrix3d(${m[0] / dw},${m[3] / dw},0,${m[6] / dw},${m[1] / dh},${m[4] / dh},0,${m[7] / dh},0,0,1,0,${m[2]},${m[5]},0,1)`;
      }
      this.el.contour.setAttribute('points', q.map((p) => p.map((v) => v.toFixed(1)).join(',')).join(' '));
      const ok = convexe(q);
      this.el.alerte.hidden = ok;
      this.el.valider.setAttribute('aria-disabled', String(!ok));
      // Les coins dessinent-ils un mur aux proportions des mesures ? Au-delà de 18 % d'écart, on le dit
      // (sans bloquer : la focale supposée peut différer, par exemple avec l'objectif 0,5×)
      const ecart = ok ? proportionVue(q, this.photo.W, this.photo.H) / this.proportionMur() : NaN;
      const sens = !Number.isFinite(ecart) ? '' : ecart < 1 / 1.18 ? 'plus étroits' : ecart > 1.18 ? 'plus larges' : '';
      this.el.proportion.hidden = !sens;
      this.el.proportion.textContent = sens ? `Coins ${sens} que ${this.dimensions()}${NBSP}: vérifiez-les.` : '';
      this.dessinerLes();
    }

    // Les lés, comme le cadreur (mb-cadreur.js, decoupe) : en millimètres, des lés entiers de 76,2 cm
    // depuis le bord gauche de la largeur imprimée, puis un dernier lé à sa largeur réelle
    lignesDesLes() {
      if (!this.cadrage || !this.coins) return [];
      const le = Math.round((parseFloat(this.dataset.le) || VCM.leCm) * 10);
      const imprime = Math.round(this.cadrage.largeur_cm * 10), marge = Math.round(this.marge() * 10);
      const visible = imprime - marge, entiers = Math.floor((imprime - 1) / le);
      const H = carreVersQuad(this.coins), lignes = [];
      for (let k = 1; k <= entiers; k++) {
        const u = (k * le - marge / 2) / visible; // la marge est coupée à moitié de chaque côté
        if (u > 0 && u < 1) lignes.push([appliquer(H, u, 0), appliquer(H, u, 1)]);
      }
      return lignes;
    }

    dessinerLes() {
      const ns = 'http://www.w3.org/2000/svg';
      this.el.lesG.replaceChildren(...this.lignesDesLes().flatMap(([a, b]) => ['fond', 'trait'].map((c) => {
        const l = document.createElementNS(ns, 'line');
        l.setAttribute('x1', a[0].toFixed(1)); l.setAttribute('y1', a[1].toFixed(1));
        l.setAttribute('x2', b[0].toFixed(1)); l.setAttribute('y2', b[1].toFixed(1));
        l.setAttribute('class', `mb-vcm__le-${c}`);
        l.setAttribute('vector-effect', 'non-scaling-stroke');
        return l;
      })));
    }

    /* ---------- Gestes : glisser un coin, toucher un coin, clavier ---------- */
    brancherGestes() {
      const scene = this.el.scene;
      scene.addEventListener('pointerdown', (e) => {
        if (this.photo && this.dialog.dataset.etape === 'meubles') { this.gesteMeubles(e); return; }
        if (!this.photo || this.dialog.dataset.etape !== 'coins') return;
        const coin = e.target.closest('[data-coin]');
        if (coin) { this.saisirCoin(e, +coin.dataset.coin, coin); return; }
        if (e.button > 0) return;
        const depart = { x: e.clientX, y: e.clientY, t: e.timeStamp };
        const fin = (f) => {
          retirer();
          if (Math.hypot(f.clientX - depart.x, f.clientY - depart.y) < 10 && f.timeStamp - depart.t < 700) this.toucher(f.clientX, f.clientY);
        };
        const retirer = () => { scene.removeEventListener('pointerup', fin); scene.removeEventListener('pointercancel', retirer); };
        scene.addEventListener('pointerup', fin);
        scene.addEventListener('pointercancel', retirer);
      });
      this.el.coins.forEach((b, i) => {
        b.addEventListener('keydown', (e) => {
          const pas = (e.shiftKey ? 10 : 1) / (this.s || 1);
          const d = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] }[e.key];
          if (!d || !this.coins) return;
          e.preventDefault();
          this.coins[i] = this.borner([this.coins[i][0] + d[0], this.coins[i][1] + d[1]]);
          this.glisse(true, i);
          this.placerTout();
          this.planifierRendu(250);
        });
      });
    }

    saisirCoin(e, i, bouton) {
      e.preventDefault();
      try { bouton.setPointerCapture(e.pointerId); } catch (err) { /* pointeur déjà relâché */ }
      const depart = { x: e.clientX, y: e.clientY, c: [...this.coins[i]] };
      let dernier = e, raf = 0;
      const poser = () => {
        raf = 0;
        this.coins[i] = this.borner([depart.c[0] + (dernier.clientX - depart.x) / this.s, depart.c[1] + (dernier.clientY - depart.y) / this.s]);
        this.placerTout();
        this.loupe(i, dernier.clientX, dernier.clientY);
      };
      const bouger = (m) => { if (m.pointerId !== e.pointerId) return; dernier = m; if (!raf) raf = requestAnimationFrame(poser); };
      const lacher = (m) => {
        if (m.pointerId !== e.pointerId) return;
        bouton.removeEventListener('pointermove', bouger);
        bouton.removeEventListener('pointerup', lacher);
        bouton.removeEventListener('pointercancel', lacher);
        if (raf) { cancelAnimationFrame(raf); dernier = m; poser(); }
        this.cacherLoupe();
        this.planifierRendu();
      };
      bouton.addEventListener('pointermove', bouger);
      bouton.addEventListener('pointerup', lacher);
      bouton.addEventListener('pointercancel', lacher);
      this.glisse(true, i);
      this.loupe(i, e.clientX, e.clientY);
    }

    // Toucher la photo place le coin le plus proche à cet endroit (alternative au glisser, WCAG 2.5.7)
    toucher(cx, cy) {
      const r = this.el.plan.getBoundingClientRect();
      const p = this.borner([(cx - r.left) / this.s, (cy - r.top) / this.s]);
      let i = 0;
      this.coins.forEach((c, j) => { if (distance(c, p) < distance(this.coins[i], p)) i = j; });
      this.coins[i] = p;
      this.glisse(true, i);
      this.placerTout();
      this.planifierRendu();
    }

    glisse(oui, i) {
      this.dialog.toggleAttribute('data-glisse', oui);
      this.el.coins.forEach((b, j) => b.toggleAttribute('data-actif', oui && j === i));
    }

    // La loupe au-dessus du doigt : la photo agrandie autour du point (px, py)
    preparerLoupe(cx, cy, px, py) {
      const L = this.el.loupe, T = VCM.loupe.taille, dpr = Math.min(window.devicePixelRatio || 1, 3);
      const c = L.firstElementChild;
      if (c.width !== Math.round(T * dpr)) { c.width = c.height = Math.round(T * dpr); }
      L.hidden = false;
      const x = borne(cx - T / 2, 8, innerWidth - T - 8);
      let y = cy - T - 48;
      if (y < 8) y = Math.min(innerHeight - T - 8, cy + 48);
      L.style.transform = `translate(${x}px, ${y}px)`;
      const ctx = c.getContext('2d'), cote = T / (this.s * VCM.loupe.zoom), k = c.width / cote;
      ctx.fillStyle = '#1f1a13';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.photo.canvas, px - cote / 2, py - cote / 2, cote, cote, 0, 0, c.width, c.height);
      return { c, ctx, cote, k, dpr };
    }

    loupe(i, cx, cy) {
      const [px, py] = this.coins[i];
      const { c, ctx, cote, k, dpr } = this.preparerLoupe(cx, cy, px, py);
      const vers = ([qx, qy]) => [(qx - px + cote / 2) * k, (qy - py + cote / 2) * k];
      const a = vers(this.coins[(i + 3) % 4]), b = vers(this.coins[i]), d = vers(this.coins[(i + 1) % 4]);
      ctx.lineWidth = 1.5 * dpr;
      ctx.strokeStyle = '#C7A35E';
      ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(d[0], d[1]); ctx.stroke();
      const m = c.width / 2, t = 9 * dpr;
      for (const [couleur, l] of [['rgba(20,16,12,0.8)', 3 * dpr], ['#FAFAF8', 1.2 * dpr]]) {
        ctx.strokeStyle = couleur; ctx.lineWidth = l;
        ctx.beginPath(); ctx.moveTo(m - t, m); ctx.lineTo(m + t, m); ctx.moveTo(m, m - t); ctx.lineTo(m, m + t); ctx.stroke();
      }
    }

    // Pendant le pinceau : la photo, le premier plan doré, et le cercle du pinceau
    loupePinceau(cx, cy, [px, py], r) {
      const { c, ctx, cote, k, dpr } = this.preparerLoupe(cx, cy, px, py);
      ctx.drawImage(this.el.premier, px - cote / 2, py - cote / 2, cote, cote, 0, 0, c.width, c.height);
      for (const [couleur, l] of [['rgba(20,16,12,0.7)', 3 * dpr], ['#FAFAF8', 1.3 * dpr]]) {
        ctx.strokeStyle = couleur; ctx.lineWidth = l;
        ctx.beginPath(); ctx.arc(c.width / 2, c.height / 2, Math.max(2, r * k), 0, Math.PI * 2); ctx.stroke();
      }
    }

    cacherLoupe() { if (this.el && this.el.loupe) this.el.loupe.hidden = true; }

    /* ---------- Premier plan : les meubles devant le mur ----------
       Le masque vit dans l'espace de la photo (0 à 255 par pixel) : changer le cadrage, les coins ou la
       luminosité ne le perd pas, une nouvelle photo le remet à zéro. Le client touche un meuble (l'IA le
       détoure dans un worker), ou peint au pinceau. Chaque geste peut être annulé. */
    entrerMeubles() {
      if (!this.photo) return;
      const { W, H } = this.photo;
      if (!this.masque) this.masque = new Uint8Array(W * H);
      this.historique = this.historique || [];
      this.pixels = this.photo.canvas.getContext('2d').getImageData(0, 0, W, H).data;
      this.majCalque(0, 0, W, H);
      this.etatAnnuler();
      this.demarrerDetourage();
      this.majTouche();
      if (!(this.ia && this.ia.etat === 'indisponible')) this.ouvrirCorriger(false);
    }

    // Un meuble est-il déjà au premier plan ? L'œuvre reste transparente et le rond pulse tant que non
    majTouche() {
      const touche = !!(this.masque && this.masque.some((v) => v > 127));
      this.dialog.toggleAttribute('data-touche', touche);
      const pinceau = !!(this.ia && this.ia.etat === 'indisponible');
      this.el.consigneMeubles.textContent = touche
        ? `Un autre meuble${NBSP}? ${pinceau ? 'Coloriez' : 'Touchez'}\u2011le. Sinon${NBSP}: Terminer.` // trait d'union insécable
        : pinceau ? 'Coloriez le meuble du doigt pour le faire passer devant l’œuvre' : 'Touchez le meuble pour le faire passer devant l’œuvre';
    }

    majBoutonMeubles() {
      const b = this.dialog.querySelector('[data-vcm-meubles] span');
      if (b) b.textContent = this.masque && this.masque.some((v) => v > 127) ? 'Modifier les meubles devant l’œuvre' : 'Mettre l’œuvre derrière un meuble';
    }

    ouvrirCorriger(oui) {
      this.el.corrigerBloc.hidden = !oui;
      this.el.corriger.setAttribute('aria-expanded', String(oui));
      this.dialog.toggleAttribute('data-corriger', oui);
      if (!oui && this.outil !== 'toucher' && !(this.ia && this.ia.etat === 'indisponible')) this.choisirOutil('toucher');
    }

    sortirMeubles() {
      this.pixels = null; // la copie de la photo (12 Mo) est rendue
      this.cacherLoupe();
    }

    choisirOutil(outil) {
      if (outil === 'toucher' && this.ia && this.ia.etat === 'indisponible') outil = 'ajouter';
      this.outil = outil;
      this.el.outils.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.vcmOutil === outil)));
      this.dialog.dataset.outil = outil;
    }

    // Le calque du premier plan montre la photo (le meuble) par-dessus l'œuvre, teintée d'or
    majCalque(x0, y0, x1, y1) {
      if (!this.pixels || !this.masque) return;
      x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(this.photo.W, x1); y1 = Math.min(this.photo.H, y1);
      const W = this.photo.W, w = x1 - x0, h = y1 - y0;
      if (w <= 0 || h <= 0) return;
      const ctx = this.el.premier.getContext('2d'), im = ctx.createImageData(w, h), d = im.data, P = this.pixels, M = this.masque;
      for (let y = 0, o = 0; y < h; y++) {
        let i = (y0 + y) * W + x0;
        for (let x = 0; x < w; x++, i++, o += 4) {
          const m = M[i];
          if (!m) continue;
          const j = i * 4;
          d[o] = P[j] * 0.72 + 56; d[o + 1] = P[j + 1] * 0.72 + 46; d[o + 2] = P[j + 2] * 0.72 + 26; d[o + 3] = m;
        }
      }
      ctx.putImageData(im, x0, y0);
    }

    // Annuler : avant chaque geste, une copie du masque ; après, on ne garde que la zone touchée
    memoriser() { this.avant = this.masque.slice(); }

    retenir(x0, y0, x1, y1) {
      const W = this.photo.W;
      x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(W, x1); y1 = Math.min(this.photo.H, y1);
      if (this.avant && x1 > x0 && y1 > y0) {
        const w = x1 - x0, zone = new Uint8Array(w * (y1 - y0));
        for (let y = y0; y < y1; y++) zone.set(this.avant.subarray(y * W + x0, y * W + x1), (y - y0) * w);
        this.historique.push({ x0, y0, x1, y1, zone });
        if (this.historique.length > 20) this.historique.shift();
      }
      this.avant = null;
      this.etatAnnuler();
    }

    annuler() {
      const p = this.historique && this.historique.pop();
      if (!p || !this.masque) return;
      const W = this.photo.W, w = p.x1 - p.x0;
      for (let y = p.y0; y < p.y1; y++) this.masque.set(p.zone.subarray((y - p.y0) * w, (y - p.y0 + 1) * w), y * W + p.x0);
      this.majCalque(p.x0, p.y0, p.x1, p.y1);
      this.etatAnnuler();
      this.majTouche();
      this.planifierRendu();
      this.el.annonce.textContent = 'Dernière action annulée.';
    }

    etatAnnuler() {
      if (this.el && this.el.annuler) this.el.annuler.setAttribute('aria-disabled', String(!(this.historique && this.historique.length)));
    }

    gesteMeubles(e) {
      if (e.button > 0 || !this.masque) return;
      const scene = this.el.scene, r = this.el.plan.getBoundingClientRect();
      const versPhoto = (cx, cy) => [(cx - r.left) / this.s, (cy - r.top) / this.s];
      if (this.outil === 'toucher') {
        const depart = { x: e.clientX, y: e.clientY, t: e.timeStamp };
        const fin = (f) => {
          retirer();
          if (Math.hypot(f.clientX - depart.x, f.clientY - depart.y) < 10 && f.timeStamp - depart.t < 700) this.detourer(...versPhoto(f.clientX, f.clientY));
        };
        const retirer = () => { scene.removeEventListener('pointerup', fin); scene.removeEventListener('pointercancel', retirer); };
        scene.addEventListener('pointerup', fin);
        scene.addEventListener('pointercancel', retirer);
        return;
      }
      // Le pinceau : un tampon rond au bord adouci, posé tous les 30 % du rayon le long du geste
      e.preventDefault();
      try { scene.setPointerCapture(e.pointerId); } catch (err) { /* pointeur déjà relâché */ }
      const ajouter = this.outil === 'ajouter', rayon = (this.el.taille.value / 2) / this.s;
      const zone = [Infinity, Infinity, -Infinity, -Infinity];
      let dernier = versPhoto(e.clientX, e.clientY), client = [e.clientX, e.clientY], file = [], raf = 0;
      this.memoriser();
      const peindre = () => {
        raf = 0;
        const b = [Infinity, Infinity, -Infinity, -Infinity];
        for (const p of file) {
          const n = Math.max(1, Math.ceil(distance(dernier, p) / Math.max(1, rayon * 0.3)));
          for (let k = 1; k <= n; k++) this.tampon(dernier[0] + ((p[0] - dernier[0]) * k) / n, dernier[1] + ((p[1] - dernier[1]) * k) / n, rayon, ajouter, zone, b);
          dernier = p;
        }
        file = [];
        this.majCalque(b[0], b[1], b[2], b[3]);
        this.loupePinceau(client[0], client[1], dernier, rayon);
      };
      const b0 = [Infinity, Infinity, -Infinity, -Infinity];
      this.tampon(dernier[0], dernier[1], rayon, ajouter, zone, b0);
      this.majCalque(b0[0], b0[1], b0[2], b0[3]);
      this.loupePinceau(client[0], client[1], dernier, rayon);
      const bouger = (m) => {
        if (m.pointerId !== e.pointerId) return;
        file.push(versPhoto(m.clientX, m.clientY)); client = [m.clientX, m.clientY];
        if (!raf) raf = requestAnimationFrame(peindre);
      };
      const lacher = (m) => {
        if (m.pointerId !== e.pointerId) return;
        scene.removeEventListener('pointermove', bouger);
        scene.removeEventListener('pointerup', lacher);
        scene.removeEventListener('pointercancel', lacher);
        if (raf) { cancelAnimationFrame(raf); peindre(); }
        this.cacherLoupe();
        this.retenir(zone[0], zone[1], zone[2], zone[3]);
        this.majTouche();
        this.planifierRendu();
      };
      scene.addEventListener('pointermove', bouger);
      scene.addEventListener('pointerup', lacher);
      scene.addEventListener('pointercancel', lacher);
    }

    tampon(cx, cy, r, ajouter, zone, b) {
      const { W, H } = this.photo, M = this.masque;
      const x0 = Math.max(0, Math.floor(cx - r)), x1 = Math.min(W, Math.ceil(cx + r) + 1);
      const y0 = Math.max(0, Math.floor(cy - r)), y1 = Math.min(H, Math.ceil(cy + r) + 1);
      if (x1 <= x0 || y1 <= y0) return;
      const dur = r * 0.6, doux = Math.max(0.5, r - dur);
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const dd = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
          if (dd >= r) continue;
          const a = dd <= dur ? 1 : (r - dd) / doux, i = y * W + x;
          if (ajouter) { const v = a * 255; if (v > M[i]) M[i] = v; } else M[i] *= 1 - a;
        }
      }
      for (const t of [zone, b]) { t[0] = Math.min(t[0], x0); t[1] = Math.min(t[1], y0); t[2] = Math.max(t[2], x1); t[3] = Math.max(t[3], y1); }
    }

    // Le masque de l'IA (plus petit que la photo) est agrandi, recalé sur les contours de la photo, son bord
    // adouci, puis ajouté au premier plan
    fusionner(src, mw, mh) {
      const { W, H } = this.photo, M = this.masque;
      let a0 = mw, b0 = mh, a1 = -1, b1 = -1;
      for (let y = 0; y < mh; y++) for (let x = 0; x < mw; x++) {
        if (src[y * mw + x] > 80) { if (x < a0) a0 = x; if (x > a1) a1 = x; if (y < b0) b0 = y; if (y > b1) b1 = y; }
      }
      if (a1 < 0) return false;
      const kx = W / mw, ky = H / mh, r = Math.max(3, Math.round(Math.max(W, H) / 256));
      const x0 = Math.max(0, Math.floor((a0 - 1) * kx) - r), x1 = Math.min(W, Math.ceil((a1 + 2) * kx) + r);
      const y0 = Math.max(0, Math.floor((b0 - 1) * ky) - r), y1 = Math.min(H, Math.ceil((b1 + 2) * ky) + r);
      const w = x1 - x0, h = y1 - y0;
      let q = new Float32Array(w * h);
      for (let y = 0; y < h; y++) {
        let sy = (y0 + y + 0.5) / ky - 0.5;
        sy = sy < 0 ? 0 : sy > mh - 1 ? mh - 1 : sy;
        const j = sy | 0, j2 = j + 1 < mh ? j + 1 : j, fy = sy - j;
        for (let x = 0; x < w; x++) {
          let sx = (x0 + x + 0.5) / kx - 0.5;
          sx = sx < 0 ? 0 : sx > mw - 1 ? mw - 1 : sx;
          const i = sx | 0, i2 = i + 1 < mw ? i + 1 : i, fx = sx - i;
          q[y * w + x] = ((src[j * mw + i] * (1 - fx) + src[j * mw + i2] * fx) * (1 - fy) + (src[j2 * mw + i] * (1 - fx) + src[j2 * mw + i2] * fx) * fy) / 255;
        }
      }
      if (this.pixels) q = this.affiner(q, x0, y0, w, h, r);
      this.memoriser();
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let t = (q[y * w + x] - 0.35) / 0.3; // bord adouci, de 35 à 65 % de confiance
          if (t <= 0) continue;
          if (t > 1) t = 1;
          const v = t * t * (3 - 2 * t) * 255, k = (y0 + y) * W + x0 + x;
          if (v > M[k]) M[k] = v;
        }
      }
      this.retenir(x0, y0, x1, y1);
      this.majCalque(x0, y0, x1, y1);
      this.planifierRendu();
      return true;
    }

    // Filtre guidé (He, Sun, Tang) : le masque suit les contours de la photo (sa luminance) au lieu
    // d'arrondir les creux, par exemple l'angle entre un dossier et un accoudoir
    affiner(p, x0, y0, w, h, r) {
      const P = this.pixels, W = this.photo.W, n = w * h, eps = 1e-3;
      const I = new Float32Array(n), II = new Float32Array(n), Ip = new Float32Array(n);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const j = ((y0 + y) * W + x0 + x) * 4, k = y * w + x;
          const l = (P[j] * 0.2126 + P[j + 1] * 0.7152 + P[j + 2] * 0.0722) / 255;
          I[k] = l; II[k] = l * l; Ip[k] = l * p[k];
        }
      }
      const moyenne = (t) => {
        // Moyenne sur une fenêtre carrée de côté 2r + 1, par image intégrale
        const S = new Float64Array((w + 1) * (h + 1)), out = new Float32Array(n);
        for (let y = 0; y < h; y++) {
          let ligne = 0;
          for (let x = 0; x < w; x++) { ligne += t[y * w + x]; S[(y + 1) * (w + 1) + x + 1] = S[y * (w + 1) + x + 1] + ligne; }
        }
        for (let y = 0; y < h; y++) {
          const ya = Math.max(0, y - r), yb = Math.min(h, y + r + 1);
          for (let x = 0; x < w; x++) {
            const xa = Math.max(0, x - r), xb = Math.min(w, x + r + 1);
            out[y * w + x] = (S[yb * (w + 1) + xb] - S[ya * (w + 1) + xb] - S[yb * (w + 1) + xa] + S[ya * (w + 1) + xa]) / ((yb - ya) * (xb - xa));
          }
        }
        return out;
      };
      const mI = moyenne(I), mp = moyenne(p), mII = moyenne(II), mIp = moyenne(Ip);
      const a = new Float32Array(n), b = new Float32Array(n);
      for (let k = 0; k < n; k++) {
        const v = mII[k] - mI[k] * mI[k], c = mIp[k] - mI[k] * mp[k];
        a[k] = c / (v + eps); b[k] = mp[k] - a[k] * mI[k];
      }
      const ma = moyenne(a), mb = moyenne(b), q = new Float32Array(n);
      for (let k = 0; k < n; k++) q[k] = ma[k] * I[k] + mb[k];
      return q;
    }

    /* ---------- Le détourage par l'IA, dans un worker chargé à la demande ---------- */
    configDetourage() {
      try {
        const c = JSON.parse(this.dataset.detourage || 'null');
        return c && ['worker', 'bundle', 'chargeur', 'wasm', 'modele'].every((k) => typeof c[k] === 'string') ? c : null;
      } catch (e) { return null; }
    }

    get iaPrete() { return !!(this.ia && this.ia.charge && !this.ia.enAttente && this.ia.etat !== 'indisponible'); }

    demarrerDetourage() {
      if (this.ia) { this.majEtatIA(); return; }
      const c = this.configDetourage();
      this.ia = { etat: 'attente', enAttente: 0, bloquees: 0, poids: c && c.poids ? String(c.poids).replace(/ /g, NBSP) : '' };
      if (!c || !window.Worker || !window.OffscreenCanvas) { this.iaIndisponible(); return; }
      const abs = (u) => new URL(u, location.href).href;
      let w = null;
      try { w = new Worker(abs(c.worker), { type: 'module' }); } catch (e) {
        // Worker servi par un autre domaine : on le lance depuis une adresse locale qui l'importe
        try { w = new Worker(URL.createObjectURL(new Blob([`import ${JSON.stringify(abs(c.worker))};`], { type: 'text/javascript' })), { type: 'module' }); } catch (e2) { w = null; }
      }
      if (!w) { this.iaIndisponible(); return; }
      this.ia.worker = w;
      this.ia.t0 = performance.now();
      w.onmessage = ({ data }) => this.recevoirIA(data);
      w.onerror = (e) => { if (e && e.preventDefault) e.preventDefault(); this.iaIndisponible(); };
      w.postMessage({ type: 'init', fichiers: { bundle: abs(c.bundle), chargeur: abs(c.chargeur), wasm: abs(c.wasm), modele: abs(c.modele) } });
      this.majEtatIA();
    }

    envoyerPhoto() {
      const ia = this.ia;
      if (!ia || !ia.worker || !ia.charge || !this.photo) return;
      ia.enAttente++;
      this.majEtatIA();
      createImageBitmap(this.photo.canvas).then((b) => {
        if (this.ia === ia && ia.worker) ia.worker.postMessage({ type: 'photo', bitmap: b }, [b]); else b.close();
      });
    }

    recevoirIA(data) {
      const ia = this.ia;
      if (!ia || ia.etat === 'indisponible') return;
      if (data.type === 'bloquee') { ia.bloquees = data.bloquees; return; }
      if (data.type === 'pret') {
        ia.charge = true;
        ia.chargement = Math.round(performance.now() - ia.t0);
        ia.octets = data.octets;
        this.mesure('detourage', { chargement_ms: ia.chargement, octets: ia.octets, bloquees: data.bloquees });
        this.envoyerPhoto();
      } else if (data.type === 'photo') {
        ia.enAttente = Math.max(0, ia.enAttente - 1);
        this.majEtatIA();
      } else if (data.type === 'masque') {
        ia.occupe = false;
        this.dialog.removeAttribute('data-calcul');
        const ok = !!(this.photo && this.masque) && this.fusionner(data.masque, data.mw, data.mh);
        this.mesure('detourage', { chargement_ms: ia.chargement, octets: ia.octets, toucher_ms: data.ms, bloquees: data.bloquees });
        this.etatIA(ok ? '' : `Rien trouvé à cet endroit${NBSP}: touchez le meuble lui-même.`);
        this.majTouche();
        if (ok) this.suivre('meuble');
        this.el.annonce.textContent = ok ? 'Le meuble passe devant l’œuvre.' : '';
      } else if (data.type === 'erreur') {
        ia.occupe = false;
        this.dialog.removeAttribute('data-calcul');
        if (data.etape === 'toucher') { this.etatIA(`Ce meuble n’a pas été reconnu${NBSP}: coloriez-le au pinceau «${NBSP}Ajouter${NBSP}».`); this.ouvrirCorriger(true); }
        else this.iaIndisponible();
      }
    }

    detourer(px, py) {
      const { W, H } = this.photo, ia = this.ia;
      if (px < 0 || py < 0 || px >= W || py >= H || !ia || ia.occupe) return;
      if (!this.iaPrete) { if (ia.etat !== 'indisponible') this.etatIA(`Encore un instant${NBSP}: l’outil se prépare.`); return; }
      ia.occupe = true;
      this.dialog.setAttribute('data-calcul', '');
      this.etatIA('Un instant…');
      ia.worker.postMessage({ type: 'toucher', x: px / W, y: py / H });
    }

    iaIndisponible() {
      if (this.ia && this.ia.worker) this.ia.worker.terminate();
      this.ia = { etat: 'indisponible' };
      if (this.dialog) this.dialog.removeAttribute('data-calcul');
      if (this.outil === 'toucher') this.choisirOutil('ajouter');
      if (this.el) { this.ouvrirCorriger(true); this.majTouche(); }
      this.majEtatIA();
    }

    majEtatIA() {
      const ia = this.ia;
      if (!ia || !this.dialog) return;
      const etat = ia.etat === 'indisponible' ? 'indisponible' : this.iaPrete ? 'pret' : 'attente';
      this.dialog.dataset.ia = etat;
      this.el.outils[0].setAttribute('aria-disabled', String(etat === 'indisponible'));
      this.etatIA({
        attente: `Préparation de l’outil${ia.poids ? ` (${ia.poids})` : ''}. Il travaille dans votre téléphone${NBSP}: votre photo n’en sort pas.`,
        pret: '', // la consigne le dit déjà
        // Outil absent (non branché, appareil trop ancien, échec) : on dit quoi faire, sans accuser l'appareil
        indisponible: '',
      }[etat]);
    }

    etatIA(texte) { if (this.el && this.el.etatIA) this.el.etatIA.textContent = texte; }

    arreterDetourage() {
      if (this.ia && this.ia.worker) this.ia.worker.terminate(); // le moteur rend sa mémoire
      this.ia = null;
    }


    /* ---------- Rendu fin : perspective, lumière de la pièce, luminosité ---------- */
    planifierRendu(delai = 30) {
      clearTimeout(this.minuteurRendu);
      this.minuteurRendu = setTimeout(() => requestAnimationFrame(() => this.rendre()), delai);
    }

    // Lumière du mur, lue dans la carte sous le quadrilatère : niveau, couleur
    lumiereDuMur(Hi) {
      const { cw, ch, carte, couleur } = this.photo.lumiere, { W, H } = this.photo;
      let n = 0, l = 0, r = 0, g = 0, b = 0;
      for (let pass = 0; pass < 2 && !n; pass++) {
        for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) {
          if (!pass) {
            const px = ((x + 0.5) * W) / cw, py = ((y + 0.5) * H) / ch;
            const [u, v] = appliquer(Hi, px, py);
            if (!(u >= 0 && u <= 1 && v >= 0 && v <= 1)) continue;
            if (this.masque && this.masque[Math.floor(py) * W + Math.floor(px)] > 127) continue; // un meuble, pas le mur
          }
          const c = y * cw + x;
          l += carte[c]; r += couleur[c * 3]; g += couleur[c * 3 + 1]; b += couleur[c * 3 + 2]; n++;
        }
      }
      const P = VCM.lumiere, ref = Math.max(0.04, l / n);
      const lum = Math.max(0.02, 0.2126 * r / n + 0.7152 * g / n + 0.0722 * b / n);
      const teinte = [r, g, b].map((v) => borne(1 + P.teinte * (v / n / lum - 1), P.teinteMin, P.teinteMax));
      const expo = borne(Math.sqrt(ref / P.murBlanc), P.expoMin, P.expoMax);
      return { ref, teinte, expo };
    }

    rendre() {
      if (!this.photo || !this.coins || !this.oeuvreImg || !this.dialog.open) return;
      this.construireTexture();
      const q = this.coins;
      if (!convexe(q) || !this.texture) { this.glisse(false); this.dialog.dataset.rendu = ''; return; }
      if (this.sansExport) { this.glisse(false); this.dialog.dataset.rendu = ''; return; }
      const t0 = performance.now();
      const { W, H } = this.photo, Hi = inverse(carreVersQuad(q));
      const sansLumiere = this.dataset.lumiere === 'non';
      const L = sansLumiere ? { ref: 1, teinte: [1, 1, 1], expo: 1 } : this.lumiereDuMur(Hi);
      this.expo = L.expo;
      const P = VCM.lumiere, { cw, ch, carte } = this.photo.lumiere;
      const x0 = Math.max(0, Math.floor(Math.min(...q.map((p) => p[0]))) - 1), x1 = Math.min(W, Math.ceil(Math.max(...q.map((p) => p[0]))) + 1);
      const y0 = Math.max(0, Math.floor(Math.min(...q.map((p) => p[1]))) - 1), y1 = Math.min(H, Math.ceil(Math.max(...q.map((p) => p[1]))) + 1);
      const ctx = this.el.rendu.getContext('2d');
      ctx.clearRect(0, 0, W, H);
      if (x1 <= x0 || y1 <= y0) { this.glisse(false); return; }
      const bw = x1 - x0, bh = y1 - y0, out = ctx.createImageData(bw, bh), d = out.data;
      const { w: tw, h: th, data: tex } = this.texture;
      const [i0, i1, i2, i3, i4, i5, i6, i7, i8] = Hi;
      const haut = distance(q[0], q[1]), bas = distance(q[3], q[2]), gauche = distance(q[0], q[3]), droite = distance(q[1], q[2]);
      const base = this.lum * L.expo, kr = base * L.teinte[0], kg = base * L.teinte[1], kb = base * L.teinte[2];
      const invRef = 1 / L.ref, lx = cw / W, ly = ch / H, M = this.masque;
      let o = 0;
      for (let y = y0; y < y1; y++) {
        const py = y + 0.5;
        for (let x = x0; x < x1; x++, o += 4) {
          const px = x + 0.5, w = i6 * px + i7 * py + i8;
          const u = (i0 * px + i1 * py + i2) / w, v = (i3 * px + i4 * py + i5) / w;
          if (u < -0.02 || u > 1.02 || v < -0.02 || v > 1.02) continue;
          // Couverture du pixel au bord du mur : des bords nets mais sans escalier
          const ew = haut + (bas - haut) * v, eh = gauche + (droite - gauche) * u;
          let cov = u * ew + 0.5, t = (1 - u) * ew + 0.5;
          if (t < cov) cov = t;
          t = v * eh + 0.5; if (t < cov) cov = t;
          t = (1 - v) * eh + 0.5; if (t < cov) cov = t;
          if (cov <= 0) continue;
          if (cov > 1) cov = 1;
          // Un meuble au premier plan : l'œuvre passe derrière lui
          if (M) { const mv = M[y * W + x]; if (mv) { cov *= 1 - mv / 255; if (cov <= 0) continue; } }
          // Texture, interpolation bilinéaire
          let tx = u * tw - 0.5, ty = v * th - 0.5;
          tx = tx < 0 ? 0 : tx > tw - 1 ? tw - 1 : tx;
          ty = ty < 0 ? 0 : ty > th - 1 ? th - 1 : ty;
          const ix = tx | 0, iy = ty | 0, fx = tx - ix, fy = ty - iy;
          const ix2 = ix + 1 < tw ? ix + 1 : ix, iy2 = iy + 1 < th ? iy + 1 : iy;
          const a = (iy * tw + ix) * 4, b = (iy * tw + ix2) * 4, c = (iy2 * tw + ix) * 4, e = (iy2 * tw + ix2) * 4;
          const w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy), w01 = (1 - fx) * fy, w11 = fx * fy;
          // Lumière de la pièce : ombres et dégradés de la photo, en partie
          let f = 1;
          if (!sansLumiere) {
            let cx = px * lx - 0.5, cy = py * ly - 0.5;
            cx = cx < 0 ? 0 : cx > cw - 1 ? cw - 1 : cx;
            cy = cy < 0 ? 0 : cy > ch - 1 ? ch - 1 : cy;
            const jx = cx | 0, jy = cy | 0, gx = cx - jx, gy = cy - jy;
            const jx2 = jx + 1 < cw ? jx + 1 : jx, jy2 = jy + 1 < ch ? jy + 1 : jy;
            const lum = carte[jy * cw + jx] * (1 - gx) * (1 - gy) + carte[jy * cw + jx2] * gx * (1 - gy)
              + carte[jy2 * cw + jx] * (1 - gx) * gy + carte[jy2 * cw + jx2] * gx * gy;
            f = 1 + P.ombres * (lum * invRef - 1);
            f = f < P.min ? P.min : f > P.max ? P.max : f;
          }
          d[o] = (tex[a] * w00 + tex[b] * w10 + tex[c] * w01 + tex[e] * w11) * kr * f;
          d[o + 1] = (tex[a + 1] * w00 + tex[b + 1] * w10 + tex[c + 1] * w01 + tex[e + 1] * w11) * kg * f;
          d[o + 2] = (tex[a + 2] * w00 + tex[b + 2] * w10 + tex[c + 2] * w01 + tex[e + 2] * w11) * kb * f;
          d[o + 3] = cov * 255;
        }
      }
      ctx.putImageData(out, x0, y0);
      this.lumCuite = this.lum;
      this.el.rendu.style.filter = '';
      this.el.brouillon.style.filter = `brightness(${(this.lum * this.expo).toFixed(3)})`;
      this.dialog.dataset.rendu = 'pret';
      this.glisse(false);
      this.mesure('rendu', { ms: Math.round(performance.now() - t0), pixels: bw * bh, texture: `${tw}×${th}` });
      this.version++; this.fichier = null; this.etatEnregistrer(); this.planifierExport();
    }

    /* ---------- L'image enregistrée ---------- */
    planifierExport() {
      clearTimeout(this.minuteurExport);
      const v = this.version;
      this.minuteurExport = setTimeout(() => this.exporter(v), 250);
    }

    async exporter(v) {
      if (this.sansExport || !this.photo || this.dialog.dataset.rendu !== 'pret') return;
      const t0 = performance.now(), { W, H } = this.photo;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const x = c.getContext('2d');
      x.drawImage(this.photo.canvas, 0, 0);
      x.drawImage(this.el.rendu, 0, 0);
      if (this.les) this.lesSurCanvas(x);
      await this.legende(x, W, H);
      const blob = await new Promise((ok) => c.toBlob(ok, 'image/jpeg', 0.9));
      c.width = c.height = 1;
      if (v !== this.version || !blob) return;
      this.fichier = new File([blob], `maison-byobu-${this.dataset.oeuvre || 'oeuvre'}-chez-moi.jpg`, { type: 'image/jpeg' });
      this.etatEnregistrer();
      this.mesure('export', { ms: Math.round(performance.now() - t0), octets: blob.size, taille: `${W}×${H}` });
    }

    lesSurCanvas(x) {
      const e = Math.max(1, Math.round(Math.max(this.photo.W, this.photo.H) / 900));
      for (const [a, b] of this.lignesDesLes()) {
        for (const [couleur, l, tirets] of [['rgba(39,32,24,0.45)', 3 * e, []], ['rgba(250,250,248,0.9)', 1.2 * e, [6 * e, 5 * e]]]) {
          x.strokeStyle = couleur; x.lineWidth = l; x.setLineDash(tirets);
          x.beginPath(); x.moveTo(a[0], a[1]); x.lineTo(b[0], b[1]); x.stroke();
        }
      }
      x.setLineDash([]);
    }

    async legende(x, W, H) {
      const texte = [this.nom, this.dataset.site || 'maisonbyobu.fr', 'aperçu indicatif'].filter(Boolean).join(' · ');
      const famille = getComputedStyle(this.dialog).fontFamily || 'sans-serif';
      let taille = Math.max(13, Math.round(Math.min(W, H) * 0.022));
      try { if (document.fonts) await document.fonts.load(`500 ${taille}px ${famille}`); } catch (e) { /* police système */ }
      const marge = Math.round(taille * 1.2);
      x.font = `500 ${taille}px ${famille}`;
      while (x.measureText(texte).width > W - 2 * marge && taille > 9) { taille--; x.font = `500 ${taille}px ${famille}`; }
      x.save();
      x.fillStyle = 'rgba(250,250,248,0.9)';
      x.shadowColor = 'rgba(20,16,12,0.6)';
      x.shadowBlur = taille * 0.6;
      x.textBaseline = 'alphabetic';
      x.fillText(texte, marge, H - marge);
      x.restore();
    }

    etatEnregistrer() {
      const b = this.el.enregistrer;
      b.setAttribute('aria-disabled', String(!this.fichier));
      b.textContent = this.fichier || this.sansExport ? 'Enregistrer l’image' : 'Préparation de l’image…';
    }

    // L'image est prête avant le toucher : iOS n'ouvre la feuille de partage que dans le geste
    enregistrer() {
      const f = this.fichier;
      if (!f) return;
      const tactile = window.matchMedia && matchMedia('(pointer: coarse)').matches;
      let partage = false;
      if (tactile && navigator.share && navigator.canShare) { try { partage = navigator.canShare({ files: [f] }); } catch (e) { partage = false; } }
      this.suivre('image_enregistree');
      if (partage) {
        navigator.share({ files: [f] }).then(() => this.signal('etape', { etape: 'enregistre', par: 'partage' })).catch(() => {});
        return;
      }
      const url = URL.createObjectURL(f), a = document.createElement('a');
      a.href = url; a.download = f.name; a.hidden = true;
      this.dialog.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      this.signal('etape', { etape: 'enregistre', par: 'telechargement' });
    }
  }

  MbVoirChezMoi.decoder = decoder; // le banc de détourage lit les photos comme le module
  MbVoirChezMoi.proportionVue = proportionVue; // vérifiée par les tests
  customElements.define('mb-voir-chez-moi', MbVoirChezMoi);
})();
